import { defineComponent as _defineComponent } from 'vue'
import { tailwindSurfaceFontClass } from 'bits/font'
import { MAX_SECTION_TITLE_LENGTH } from 'bits/numbers'
import OzBaseButton from 'library/v4/components/OzBaseButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import type { WallViz } from 'types'
import type { PropType, Ref } from 'vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import MultilineInput from 'vuecomponents/multiline_input.vue'
import { useResizeObserver } from 'vuecomposables/resize_observer'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceSectionTitle',
  props: {
  /**
   * Background color for the component.
   * Default is Light UI 400 / Dark UI 400.
   */
  backgroundColor: {
    type: String,
    default: () => undefined,
  },
  /**
   * Whether the title can be edited inline or not.
   */
  canEditInline: {
    type: Boolean,
    required: true,
  },
  /**
   * Dark mode.
   */
  darkMode: {
    type: Boolean,
    required: true,
  },
  /**
   * Direction.
   */
  dir: {
    type: String as PropType<'ltr' | 'rtl' | 'auto'>,
    default: 'auto',
  },
  /**
   * Edit mode.
   */
  editMode: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether to show More button (in Wall format).
   */
  xMoreButton: {
    type: Boolean,
    default: true,
  },
  /**
   * Surface font variant.
   */
  fontId: {
    type: Number as PropType<1 | 2 | 3 | 4>,
    required: true,
  },
  /**
   * Wall format.
   */
  format: {
    type: String as PropType<WallViz>,
    required: true,
    validator(value: string): boolean {
      // We only have sections in these formats
      return ['grid', 'matrix', 'shelf', 'stream', 'map', 'timeline', 'timeline_v2'].includes(value)
    },
  },
  /**
   * Section title.
   */
  title: {
    type: String,
    required: true,
  },
  /**
   * Section id
   */
  sectionId: {
    type: Number,
    required: true,
  },
},
  emits: ["click", "open-actions", "start-editing", "finish-editing", "cancel-editing", "resize"],
  setup(__props, { expose, emit }: { emit: ({
  (name: 'click', e: MouseEvent): void
  (name: 'open-actions', e?: MouseEvent): void
  (name: 'start-editing'): void
  (name: 'finish-editing', value: string): void
  (name: 'cancel-editing'): void
  (name: 'resize'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





expose({
  selectTitleInput,
})

const isGrid = computed(() => props.format === 'grid')
const isMatrix = computed(() => props.format === 'matrix')
const isShelf = computed(() => props.format === 'shelf')
const isStream = computed(() => props.format === 'stream')
const isMap = computed(() => props.format === 'map')
const isTimeline = computed(() => props.format === 'timeline' || props.format === 'timeline_v2')
const twFontClass = computed(() => tailwindSurfaceFontClass(isMap.value ? 1 : props.fontId))

const truncatedTitle = computed(() => {
  if ((isGrid.value || isShelf.value) && props.title.length > MAX_SECTION_TITLE_LENGTH) {
    return props.title.slice(0, MAX_SECTION_TITLE_LENGTH) + '…'
  }
  return props.title
})

// Only open in edit mode if user can edit inline
const isBeingEdited = computed(() => (props.canEditInline ? props.editMode : false))
const titleInput = ref<InstanceType<typeof MultilineInput>>()

function selectTitleInput(): void {
  titleInput.value?.select()
}

// Select input if in edit mode
watch(
  isBeingEdited,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        selectTitleInput()
      })
    }
  },
  { immediate: true },
)

const currentTitle = ref(props.title)
const previousTitle = ref(props.title)

watch(
  () => props.title,
  (newTitle) => {
    previousTitle.value = newTitle
    currentTitle.value = newTitle
  },
)

function tryEditingInline(): void {
  if (!props.canEditInline) return
  emit('start-editing')
}

function finishEditing(): void {
  previousTitle.value = currentTitle.value
  emit('finish-editing', currentTitle.value)
}

function cancelEditing(): void {
  currentTitle.value = previousTitle.value
  emit('cancel-editing')
}

const $el = ref<HTMLElement>() as Ref<HTMLElement>
const skewedEdgeWidth = ref(0)

function updateSkewedEdgeWidth(): void {
  // What is this math sorcery?
  //
  // The requirement for the skewed angle is 10 degrees.
  // We can get the opposite side of the triangle by using the tangent function.
  // The additional 5 is the border radius of the skewed edge, as the edge is shifted by it.
  // Visual explanation: TBA
  skewedEdgeWidth.value = ($el.value?.getBoundingClientRect()?.height ?? 0) * Math.tan((10 * Math.PI) / 180) + 5
}

function finishEditingIfFocusTargetIsOutside(e: FocusEvent): void {
  // When MultilineInput loses focus, we don't want to emit `finish-editing` too early
  // because it may be a Tab press to focus on the Done button. To prevent this, we
  // check `e.relatedTarget` to see if it's an element inside the section title or not.
  // If it's not, then we can safely emit the event.
  if (!$el.value) return
  if (!$el.value.contains(e.relatedTarget as Node | null)) {
    emit('finish-editing', currentTitle.value)
  }
}

useResizeObserver($el, () => {
  updateSkewedEdgeWidth()
  emit('resize')
})

onMounted(() => {
  updateSkewedEdgeWidth()
})

return { __sfc: true,props, emit, isGrid, isMatrix, isShelf, isStream, isMap, isTimeline, twFontClass, truncatedTitle, isBeingEdited, titleInput, selectTitleInput, currentTitle, previousTitle, tryEditingInline, finishEditing, cancelEditing, $el, skewedEdgeWidth, updateSkewedEdgeWidth, finishEditingIfFocusTargetIsOutside, OzBaseButton, OzIcon, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset, MultilineInput }
}

})